import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import { Stack, useTheme } from '@mui/system';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import { useCurrentUserQuery, useProviderRegisterMutation, useProviderUserDeleteMutation, useRegisterConfigQuery, useUpdatePasswordFirstLogMutation, useVerifyTokenMutation } from 'src/context/api/auth';
import { useLocales } from 'src/locales';
import * as Yup from 'yup'
import { jwtDecode } from 'jwt-decode'
import { useAcceptInvitationMutation, useInvitationsQuery } from 'src/context/api/workspaces';
import { useRouter } from 'src/routes/hooks';
import { paths, ROOTS } from 'src/routes/paths';
import { useAuth0 } from '@auth0/auth0-react';
import Iconify from 'src/components/iconify';
import { enqueueSnackbar } from 'notistack';
import { useAuth } from 'src/auth';
import SSOProviderLoader from './sso-provider-loader';

export default function UpdatePassword() {
    const { t } = useLocales();
    const [searchParams] = useSearchParams();
    const { data: config, isLoading: isLoadingConfig } = useRegisterConfigQuery(searchParams.get('token') ?? '');
    const [updatePassword,] = useUpdatePasswordFirstLogMutation();
    const [acceptInvitation,] = useAcceptInvitationMutation();
    const [providerRegister,] = useProviderRegisterMutation();
    const theme = useTheme();
    const [decodedToken, setDecodedToken]: any = useState();
    const router = useRouter();
    const [isExpired, setIsExpired] = useState(false);
    const [tokens, setTokens] = useState<any>([]);
    const invitations: any = useInvitationsQuery(null);
    const { user, reload } = useAuth();
    const [verifyToken, responseVerify] = useVerifyTokenMutation();
    const { loginWithPopup, getAccessTokenSilently, isAuthenticated, user: auth0User, error } = useAuth0();
    const [provider, setProvider] = useState<any>('');
    const [providerUserDelete] = useProviderUserDeleteMutation();
    const [hasVoluntaryClickedONthButton, setHasVoluntaryClickedONthButton] = useState(false);

    const [isLoadingSSO, setIsLoadingSSO] = useState(false);

    const handleProviderAuth = async () => {
        try {
            const token: any = await getAccessTokenSilently();
            const response: any = await providerRegister({ token, provider, invite_token: searchParams.get('token')?.replace('token=', '') ?? '' })
            if (response && response.data && response.data.status === 200) {
                reload()
            } else {
                console.error(response)
                enqueueSnackbar('Failed to register user. Status: ' + response?.status, {
                    variant: 'error',
                });
            }
        } catch (error) {
            console.error(error)
            enqueueSnackbar('Error during sign up', {
                variant: 'error',
            });
        } finally {
            setIsLoadingSSO(false);
        }

    }

    const signUpWithProvider = async (provider: any) => {
        setProvider(provider)
        setIsLoadingSSO(true);
        try {
            setHasVoluntaryClickedONthButton(true);
            await loginWithPopup({
                authorizationParams: {
                    connection: provider,
                }
            }).then(async (response) => {
                await handleProviderAuth()
            })
        } catch (error) {
            enqueueSnackbar('Error : Error during linking account', {
                variant: 'error',
            });
        } finally {
            setIsLoadingSSO(false);
        }
    }

    useEffect(() => {
        if (decodedToken) {
            setTokens(decodedToken.token);
        }
    }, [decodedToken])

    const { schema, defaultValues } = config?.eidp ? {
        schema: Yup.object({
            firstname: Yup.string().required(),
            lastname: Yup.string().required(),
            email: Yup.string().required(),
            token: Yup.string().required(),
        }),
        defaultValues: {
            firstname: '',
            lastname: '',
            email: '',
            token: '',
        }
    } : {
        schema: Yup.object({
            password: Yup.string().required(),
            lastname: Yup.string().required(),
            firstname: Yup.string().required(),
            confirmPassword: Yup.string().required(),
            token: Yup.string().required(),
        }),
        defaultValues: {
            password: '',
            confirmPassword: '',
            firstname: '',
            lastname: '',
        }
    };

    const methods = useForm({
        resolver: yupResolver(schema as any),
        defaultValues: defaultValues as any,
    });

    const { formState: { isSubmitting } } = methods;

    useEffect(() => {
        if (searchParams.has('token') && config) {
            if (config?.eidp) {
                methods.setValue("token", searchParams.get('token'));
                methods.setValue("email", config?.email);
            }

            try {
                setDecodedToken(jwtDecode(searchParams.get('token') as any));
            } catch (error) {
                console.error('Invalid token:', error);
            }
        }
        else {
            methods.setValue("token", searchParams.get('token'));
        }
    }, [searchParams, config])

    const handleUpdatePassword = async (data: { password: string; confirmPassword: string; token: string; lastname: string; firstname: string }) => {
        if (config?.eidp) {
            const searchParams = new URLSearchParams();
            searchParams.set('token', data.token);
            searchParams.set('firstname', data.firstname);
            searchParams.set('lastname', data.lastname);
            window.location.href =process.env.CROSS_API_URL + `/auth/register?${searchParams.toString()}`
        } else {
            try {
                await updatePassword(data)
                const workspaceIds = decodedToken.workspaces.map((ws: any) => ws.id);
                const tokens = decodedToken.token;

                const acceptAllInvitations = workspaceIds.map((workspaceId: number, index: number) => {
                    const token = tokens[index];
                    return acceptInvitation({ id: workspaceId, token });
                });

                const invitations = await Promise.all(acceptAllInvitations);
                reload()
                if (invitations.length > 1) {
                    router.push(ROOTS.WORKSPACES);
                } else if (invitations.length === 1) {
                    router.push(paths.dash.brandOverview);
                }
                else {
                    router.push(ROOTS.AUTH);
                }
            } catch (error) {
                console.error("Failed to update password or accept invitations:", error);
                enqueueSnackbar('Error : Error during sign up', {
                    variant: 'error',
                });
            }
        }
    };

    useEffect(() => {
        if (tokens.length >= 1) {
            tokens.map((token: any, index: number) => {
                verifyToken({ token: token })
                    .then((response) => {
                        const data: any = Object.values(response)[0];
                        const status = data.status;
                        if (status != 200) {
                            setIsExpired(true);
                        }
                    })
                    .catch(error => {
                        console.error('Error :', error);
                    });
            });
        }
    }, [tokens, verifyToken])

    const renderContent = () => {
        if (isLoadingConfig) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                </Box>
            )
        }


        if (isExpired) {
            return (
                <Typography variant="h4" style={{ color: '#1C252E', textAlign: 'center' }}>
                    Your token has expired
                </Typography>
            )
        }

        return !isExpired && (
            <Box sx={{ display: isLoadingSSO ? 'none' : 'block' }}>

                <FormProvider methods={methods} onSubmit={methods.handleSubmit(handleUpdatePassword)}>
                    <Stack spacing={2} sx={{ mb: 2 }}>
                        <Stack spacing={2} sx={{ position: 'relative' }}>
                            <Typography variant="h4" style={{ color: '#1C252E', textAlign: 'center' }}>
                                Create your account
                            </Typography>
                        </Stack>


                        <Alert sx={{ mb: 4 }} severity="info">
                            You have been invited by <span style={{ fontWeight: 'bold' }}>{decodedToken?.user?.profile?.firstname} {' '} {decodedToken?.user?.profile?.lastname} </span>
                            to the
                            <span style={{ fontWeight: 'bold' }}>
                                {decodedToken?.workspaces.length > 1 ? ' workspaces: ' : ' workspace: '}
                                {decodedToken?.workspaces.map((ws: any, index: number) => (
                                    <span key={index}>
                                        {ws?.name}
                                        {index < decodedToken?.workspaces.length - 1 && ', '}
                                    </span>
                                ))}
                            </span>.
                        </Alert>



                        <div style={{ display: 'flex', flexDirection: "row", gap: 10 }}>

                            <RHFTextField
                                name="firstname"
                                label={"Firstname"}
                                InputLabelProps={{
                                    sx: {
                                        color: '#1C252E',
                                        '&.Mui-focused': {
                                            color: '#1C252E !important',
                                        },
                                    },
                                }}
                                InputProps={{
                                    sx: {
                                        color: '#1C252E',
                                    },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#637381',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#637381',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#4c5b6e',
                                        },
                                        '& input:-webkit-autofill': {
                                            '-webkit-box-shadow': '0 0 0 1000px white inset',
                                            '-webkit-text-fill-color': '#1C252E',
                                        },
                                    },
                                }}
                            />

                            <RHFTextField
                                name="lastname"
                                label={"Lastname"}
                                InputLabelProps={{
                                    sx: {
                                        color: '#1C252E',
                                        '&.Mui-focused': {
                                            color: '#1C252E !important',
                                        },
                                    },
                                }}
                                InputProps={{
                                    sx: {
                                        color: '#1C252E',
                                    },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#637381',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#637381',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#4c5b6e',
                                        },
                                        '& input:-webkit-autofill': {
                                            '-webkit-box-shadow': '0 0 0 1000px white inset',
                                            '-webkit-text-fill-color': '#1C252E',
                                        },
                                    },
                                }}
                            />

                        </div>

                        {config?.eidp &&
                            <RHFTextField
                                name="email"
                                value={config?.email}
                                disabled
                            />
                        }

                        {config?.eidp && (
                            <Button variant='contained'
                                color="inherit"
                                size="large"
                                type="submit"
                                fullWidth

                                sx={{ backgroundColor: '#1C252E', color: 'white' }}>
                                Accept Invitation
                            </Button>
                        )}

                        {!config?.eidp && (
                            <>
                                <RHFTextField
                                    name="password"
                                    label={"Password"}
                                    InputLabelProps={{
                                        sx: {
                                            color: '#1C252E',
                                            '&.Mui-focused': {
                                                color: '#1C252E !important',
                                            },
                                        },
                                    }}
                                    type='password'
                                    InputProps={{
                                        sx: {
                                            color: '#1C252E',
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: '#637381',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#637381',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#4c5b6e',
                                            },
                                            '& input:-webkit-autofill': {
                                                '-webkit-box-shadow': '0 0 0 1000px white inset',
                                                '-webkit-text-fill-color': '#1C252E',
                                            },
                                        },
                                    }}
                                />

                                <RHFTextField
                                    name="confirmPassword"
                                    label={"Repeat password"}
                                    InputLabelProps={{
                                        sx: {
                                            color: '#1C252E',
                                            '&.Mui-focused': {
                                                color: '#1C252E !important',
                                            },
                                        },
                                    }}
                                    type='password'
                                    InputProps={{
                                        sx: {
                                            color: '#1C252E',
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: '#637381',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#637381',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#4c5b6e',
                                            },
                                            '& input:-webkit-autofill': {
                                                '-webkit-box-shadow': '0 0 0 1000px white inset',
                                                '-webkit-text-fill-color': '#1C252E',
                                            },
                                        },
                                    }}
                                />
                                <LoadingButton
                                    color="inherit"
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    loading={isSubmitting}
                                    sx={{ backgroundColor: '#1C252E', color: 'white' }}
                                >
                                    Accept invitation
                                </LoadingButton>
                            </>
                        )}
                    </Stack>

                    {!config?.eidp && (


                        <>
                            <Divider><Typography color='black' variant='subtitle1'> or</Typography></Divider>

                            <Stack direction="column" spacing={1} sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>

                                <Button
                                    variant='outlined'
                                    sx={{
                                        height: '50px',
                                        borderRadius: '8px',
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        gap: 5,
                                        transition: 'background-color 0.3s, border-color 0.3s',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                        },
                                    }}
                                    onClick={() => signUpWithProvider('google-oauth2')}
                                >

                                    <Iconify icon="logos:google-icon" width={24} />
                                    <Typography variant="subtitle1" color='black'>Sign up with Google</Typography>
                                </Button>
                            </Stack>
                        </>
                    )}
                </FormProvider>
            </Box>

        )
    }


    return (
        <div style={{
            width: '100%',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundImage: 'url(/assets/background/background.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
        }}>
            <div style={{
                width: '100%',
                maxWidth: '400px',
                backgroundColor: 'white',
                borderRadius: '10px',
                padding: '40px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                position: 'relative',
            }}>

                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 25 }}>
                    <div style={{
                        width: '100px',
                        height: '100px',

                        position: 'relative',
                        borderRadius: '50%',
                        overflow: 'hidden',
                    }}>
                        <div style={{
                            width: '100%',
                            height: '100%',
                            backgroundImage: 'linear-gradient(180deg, rgba(0, 167, 111, 1) -100%, rgba(0, 167, 111, 0.22) 100%)',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: 0,
                            opacity: 0.3,
                            borderRadius: '50%',
                        }}>

                        </div>
                        <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative',
                            zIndex: 1,
                        }}>
                            <img
                                src='/assets/logo.png'
                                alt="Logo"
                                style={{
                                    width: '60px',
                                    height: 'auto',
                                }}
                            />
                        </div>
                    </div>
                </div>
                <>
                    {renderContent()}
                    {isLoadingSSO && (
                        <Box sx={{
                            height: '100%',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            borderRadius: '10px',
                            padding: 2,
                            zIndex: 100,
                        }}>
                            <Box sx={{ width: '100%', height: '100%', display: 'flex', gap: 5, flexDirection: 'column', }}>
                                <CircularProgress size={50} sx={{ marginRight: 1, alignSelf: 'center' }} variant='indeterminate' value={50} />
                                <Typography variant="h6" color={'black'} fontWeight={'bold'} sx={{ textAlign: 'center' }}>
                                    SSO authentication is in progress...
                                </Typography>
                            </Box>

                        </Box>
                    )}
                </>
            </div>
        </div>
    );
}